import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { toast } from 'react-toastify';
import http from '../../services/http';

/**
 * A observação do cliente, fixa para quem atende.
 *
 * ## Por que existe
 *
 * Pedido da Gisele (19/10/2026): "observação do cliente -- fixo no ticket e no
 * mural". Decisão do dono do produto em 20/10: a observação é da EMPRESA, e
 * aparece nos dois lugares -- aqui (atendimento e chamado) e no mural.
 *
 * O que se quer avisar -- "contrato suspenso", "só fala com o titular" -- é
 * verdade sobre a empresa, não sobre o telefone que ligou. Por isso o campo
 * mora em `companies`, e não em `clients`: num contato, a frase teria de ser
 * repetida em cada pessoa, e bastaria um contato novo (eles nascem sozinhos,
 * pelo WhatsApp) para o aviso desaparecer na conversa de quem nunca ligou.
 *
 * ## Painel, e não mais uma linha da ficha
 *
 * A ficha do cliente já tem uma dúzia de linhas de 13px -- Código, Endereço,
 * Representante, Fase, Situação. Uma observação ali teria o mesmo peso visual
 * de um CEP, e ela é justamente o que muda COMO se atende. Vem destacada, com
 * fundo próprio e o alfinete, e antes de tudo o resto.
 *
 * ## A permissão
 *
 * `#observacao-cliente`, própria -- separada de "editar dados do cliente" por
 * decisão do dono do produto em 20/10: corrigir um telefone afeta um cadastro,
 * esta frase aparece para a equipe inteira, inclusive no mural. Sem a permissão
 * o painel é só leitura, e não aparece nada clicável: um controle que parece
 * editável e responde 403 é pior que nenhum controle.
 *
 * Quem não tem a permissão e o cliente não tem observação não vê NADA -- nem
 * um painel vazio, que só ocuparia a ficha para dizer que não há nada a dizer.
 */
export const PERMISSAO_OBSERVACAO = '#observacao-cliente';

const LIMITE = 2000;

/**
 * A assinatura embaixo do texto.
 *
 * Uma observação sem autor nem data envelhece mal: "contrato suspenso",
 * escrito por alguém, algum dia, não diz se ainda vale. A data é formatada pelo
 * locale do navegador, como no resto da tela.
 */
const assinatura = (empresa) => {
    const autor = empresa?.observacao_autor?.name || '';
    const quando = empresa?.observacao_updated_at
        ? new Date(empresa.observacao_updated_at).toLocaleString()
        : '';

    if (!autor && !quando) return '';
    if (!autor) return quando;
    if (!quando) return autor;

    return `${autor} · ${quando}`;
};

const ObservacaoDoCliente = ({ empresa, onMudou, compacto = false }) => {
    const [aberto, setAberto] = useState(false);
    const [rascunho, setRascunho] = useState('');
    const [salvando, setSalvando] = useState(false);
    // O que esta tela mostra AGORA.
    //
    // Mesma razão do controle da fase: as duas telas que usam este painel
    // guardam a empresa de formas diferentes (estado local no cartão do
    // chamado, vindo de cima no chat). Guardar aqui o que acabou de ser salvo
    // evita propagar para cima em uma delas, e na próxima carga o valor vem do
    // servidor de qualquer jeito.
    const [recemSalvo, setRecemSalvo] = useState(null);

    const permissoes = useSelector((s) => s.user?.permissions) || [];
    const usuario = useSelector((s) => s.user?.currentUser);
    const podeEditar = Boolean(usuario?.is_super_admin)
        || (Array.isArray(permissoes) && permissoes.includes(PERMISSAO_OBSERVACAO));

    const empresaAtual = recemSalvo || empresa;
    const texto = (empresaAtual?.observacao || '').trim();

    if (!empresa?.id) return null;
    if (!texto && !podeEditar) return null;

    const abrir = () => {
        setRascunho(texto);
        setAberto(true);
    };

    /**
     * `valorExplicito` existe para o botao "Remover".
     *
     * `setRascunho('')` seguido de `salvar()` NAO limpa nada: `salvar` leria
     * `rascunho` do render em que foi criada -- o texto antigo --, e o botao
     * "Remover" salvaria exatamente o que devia apagar. O teste pegou isto.
     */
    const salvar = async (valorExplicito) => {
        const novo = (valorExplicito !== undefined ? valorExplicito : rascunho).trim();
        setSalvando(true);
        try {
            const { data } = await http.put(`/companies/${empresa.id}/observacao`, {
                // Vazio é APAGAR, e não string vazia: é assim que o operador
                // desfaz uma observação que não vale mais, sem abrir cadastro.
                observacao: novo === '' ? null : novo,
            });
            setRecemSalvo(data || null);
            setAberto(false);
            if (onMudou) onMudou(data);
            toast.success(novo === '' ? 'Observação removida.' : 'Observação salva.');
        } catch (err) {
            // Avisa, e NÃO muda o que está na tela nem fecha a caixa: um painel
            // que mostra o texto novo sem ter salvado é pior que um erro -- o
            // operador sai achando que registrou.
            toast.error(
                err?.response?.data?.message
                || 'Não foi possível salvar a observação do cliente.'
            );
        } finally {
            setSalvando(false);
        }
    };

    /*
     * As cores saem da paleta `warning`, nunca de hex.
     *
     * O painel precisa chamar atenção sem parecer erro (o vermelho de `error`
     * diria "algo falhou"), e um hex fixo ficaria ilegível em um dos dois temas
     * -- `alpha()` sobre a cor do tema clareia no escuro e escurece no claro
     * sozinho.
     */
    const corDaBorda = (t) => alpha(t.palette.warning.main, 0.45);
    const corDoFundo = (t) => alpha(t.palette.warning.main, 0.1);

    const caixa = () => {
        if (!podeEditar) return null;

        return (
            <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontSize: '1rem' }}>Observação do cliente</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1.5 }}>
                        Fica fixa no atendimento e no chamado deste cliente, e é publicada no
                        mural. Vale para qualquer contato desta empresa.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={10}
                        value={rascunho}
                        onChange={(e) => setRascunho(e.target.value.slice(0, LIMITE))}
                        inputProps={{ maxLength: LIMITE, 'data-testid': 'observacao-do-cliente-campo' }}
                        placeholder="O que quem atender precisa saber antes de falar com este cliente."
                        helperText={`${rascunho.length}/${LIMITE}`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAberto(false)} disabled={salvando}>Cancelar</Button>
                    {/*
                      "Remover" só aparece quando há o que remover, e é o mesmo
                      caminho do salvar com texto vazio -- a api trata vazio
                      como apagar.
                    */}
                    {texto && (
                        <Button
                            color="warning"
                            disabled={salvando}
                            data-testid="observacao-do-cliente-remover"
                            onClick={() => { setRascunho(''); salvar(''); }}
                        >
                            Remover
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        // `() => salvar()` e nao `salvar`: ligada direto, o
                        // onClick passaria o EVENTO como `valorExplicito`.
                        onClick={() => salvar()}
                        disabled={salvando || rascunho.trim() === texto}
                        data-testid="observacao-do-cliente-salvar"
                    >
                        {salvando ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    if (!texto) {
        // Sem observação, com permissão: só o convite, discreto. Um painel
        // vazio e destacado gritaria por atenção para dizer que não há nada.
        return (
            <>
                <Box
                    component="button"
                    type="button"
                    data-testid="observacao-do-cliente-adicionar"
                    onClick={abrir}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        font: 'inherit',
                        fontSize: '11.5px',
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                    }}
                >
                    <PushPinOutlinedIcon sx={{ fontSize: 13 }} />
                    Adicionar observação
                </Box>
                {caixa()}
            </>
        );
    }

    return (
        <>
            <Box
                data-testid="observacao-do-cliente"
                onClick={podeEditar ? abrir : undefined}
                role={podeEditar ? 'button' : undefined}
                tabIndex={podeEditar ? 0 : undefined}
                aria-label={podeEditar ? 'Editar a observação do cliente' : undefined}
                onKeyDown={podeEditar ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        abrir();
                    }
                } : undefined}
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    px: 1,
                    py: compacto ? 0.5 : 0.75,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: corDaBorda,
                    backgroundColor: corDoFundo,
                    cursor: podeEditar ? 'pointer' : 'default',
                    ...(podeEditar ? { '&:hover': { borderStyle: 'dashed' } } : null),
                }}
            >
                <PushPinOutlinedIcon
                    sx={{ fontSize: 14, mt: '2px', color: 'warning.main', flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                    {/*
                      `pre-wrap` porque o operador escreve em linhas -- "1)
                      ...", "2) ..." -- e colapsar isso num parágrafo só
                      embaralharia o aviso. `break-word` para o painel estreito
                      do chat não ser estourado por um link colado.
                    */}
                    <Typography
                        sx={{
                            fontSize: compacto ? '11.5px' : '12.5px',
                            fontWeight: 600,
                            lineHeight: 1.45,
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                            color: 'text.primary',
                        }}
                    >
                        {texto}
                    </Typography>
                    {assinatura(empresaAtual) && (
                        <Typography
                            sx={{ fontSize: '10.5px', color: 'text.secondary', mt: 0.25 }}
                        >
                            {assinatura(empresaAtual)}
                        </Typography>
                    )}
                </Box>
                {salvando && <CircularProgress size={12} sx={{ ml: 'auto' }} />}
            </Box>
            {caixa()}
        </>
    );

};

export default ObservacaoDoCliente;
